import { cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { Suspense } from 'react';
import { DonationsIcon } from './assets/donations-icon';
import { CollectedIcon } from './assets/collected-icon';
import { EmptyChar } from './empty-char';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { usePortalCampaignQuery, usePortalMetadataQuery, usePortalCampaignAggregateQuery } from '@/lib/data-hooks';
import { PortalAnimateNumber } from './portal-animate-number';
import { useClient } from '@worldcoo/ui/hooks/use-client';
import { useDebounce } from '@worldcoo/ui/hooks/use-debounce';
import { isNotFound } from '@tanstack/react-router';
import { RingProgressBar } from './ring-progress-bar';

export function CampaignStatistics(props: {
	campaignId: string;
	textEffectDisabled?: boolean;
	variant?: 'sm';
	/**
	 * Adds a "Funded" column next to the goal, shown below `lg` only. On mobile the
	 * sidebar progress card is hidden, so this is where the percentage lives.
	 */
	showFunded?: boolean;
}) {
	const { t } = useLingui();
	const metadata = usePortalMetadataQuery();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);

	// Hide component when metadata or aggregates are unavailable
	if (isNotFound(metadata)) return null;
	if (!aggregates || !('aggregates' in aggregates)) {
		return null;
	}

	if (metadata.hideStatsOnActiveCampaigns && !aggregates.aggregates[props.campaignId]?.finished) {
		return null;
	}

	return (
		<>
			<div
				className={cn(
					'bg-background flex gap-6 sm:gap-10 lg:gap-16 justify-between lg:justify-start',
					metadata.hideCollected && 'justify-center',
					props.variant === 'sm' && 'gap-16',
				)}
			>
				<div className="flex flex-col font-medium">
					<DonationsIcon className={cn('text-primary mb-2', props.variant === 'sm' && 'size-6')} />
					<div className={cn('whitespace-nowrap text-xl xl:text-2xl', props.variant === 'sm' && 'text-xl xl:text-xl')}>
						<Suspense fallback={<EmptyChar />}>
							<CampaignDonations campaignId={props.campaignId} textEffectDisabled={props.textEffectDisabled} />
						</Suspense>
					</div>
					<span
						className={cn(
							'text-primary text-nowrap text-sm font-semibold lg:text-base',
							props.variant === 'sm' && 'text-sm lg:text-sm',
						)}
					>
						{t`Donations`}
					</span>
				</div>

				{!metadata.hideCollected && (
					<div className="flex flex-col font-medium">
						<CollectedIcon className={cn('text-primary mb-2', props.variant === 'sm' && 'size-6')} />
						<div
							className={cn('whitespace-nowrap text-xl xl:text-2xl', props.variant === 'sm' && 'text-xl xl:text-xl')}
						>
							<Suspense fallback={<EmptyChar />}>
								<CampaignCollected campaignId={props.campaignId} textEffectDisabled={props.textEffectDisabled} />
							</Suspense>
						</div>
						<div
							className={cn(
								// Left to wrap below `lg`: a large goal ("Objetivo de 1.500.000 €") would
								// otherwise force the row past the viewport once the funded column is added.
								'text-primary text-sm font-semibold lg:text-nowrap lg:text-base',
								props.variant === 'sm' && 'text-nowrap text-sm lg:text-sm',
							)}
						>
							<Suspense fallback={<EmptyChar />}>
								<CampaignCollectedFromBudget campaignId={props.campaignId} />
							</Suspense>
						</div>
					</div>
				)}

				{props.showFunded && (
					<Suspense fallback={null}>
						<CampaignFunded campaignId={props.campaignId} />
					</Suspense>
				)}
			</div>
		</>
	);
}

function CampaignFunded(props: { campaignId: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const isClient = useClient();

	const campaignAggregate =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId] : undefined;
	const progress = useDebounce(!isClient || campaignAggregate?.funding == null ? 0 : campaignAggregate.progress, 150);

	// Mirrors CampaignProgress: without a funding target there is no percentage to show.
	if (campaignAggregate?.funding == null) return null;

	return (
		<div className="flex flex-col font-medium lg:hidden">
			<RingProgressBar
				radius={40}
				stroke={6}
				className="mb-2 size-8"
				inset={false}
				progress={Math.min(progress, 100)}
				strokeColor="var(--primary)"
				color="var(--primary)"
			/>
			<div className="text-xl whitespace-nowrap">
				<PortalAnimateNumber locale={language} value={progress} suffix="%" />
			</div>
			<span className="text-primary text-sm font-semibold text-nowrap">{t`Funded`}</span>
		</div>
	);
}

function CampaignDonations(props: { campaignId: string; textEffectDisabled?: boolean }) {
	const { language } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const isClient = useClient();

	if (isNotFound(campaign)) return null;

	const number = useDebounce(
		!campaign.isPageLoaded && !props.textEffectDisabled && !isClient
			? 0
			: aggregates && 'aggregates' in aggregates
				? (aggregates.aggregates[props.campaignId]?.donors ?? 0)
				: 0,
		0,
	);

	return <PortalAnimateNumber testId="donations" locale={language} value={number} />;
}

function CampaignCollected(props: { campaignId: string; textEffectDisabled?: boolean }) {
	const { language } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const isClient = useClient();

	if (isNotFound(campaign)) return null;

	const number = useDebounce(
		!campaign.isPageLoaded && !props.textEffectDisabled && !isClient
			? 0
			: aggregates && 'aggregates' in aggregates
				? (aggregates.aggregates[props.campaignId]?.collected ?? 0)
				: 0,
		0,
	);

	return <PortalAnimateNumber testId="collected" locale={language} value={number} suffix="€" decimalDigits={2} />;
}

function CampaignCollectedFromBudget(props: { campaignId: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const funding =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId]?.funding : undefined;

	if (!funding) return t`Collected`;
	const goal = formatNumber(language, funding, 0);
	return t`Goal of ${goal}` + ' €';
}
